import { signal } from '@angular/core';

const DISPLAY_LOCALE = 'en-US';

/**
 * The IANA zone every formatter in the app renders instants in. `undefined` means the
 * browser's own zone. Kept as a module-level signal (not a service) so plain functions and
 * pipes can read it, and any `computed()`/template that formats a date re-evaluates when the
 * user switches zones. TimezoneService is the only writer.
 */
export const displayTimeZone = signal<string | undefined>(undefined);

/**
 * The label printed after reporting timestamps: the facility state's zone id as stored (e.g.
 * "Central Standard Time") or, in Local mode, the local zone's standard-time name. Published by
 * TimezoneService alongside `displayTimeZone`.
 */
export const displayTimeZoneName = signal<string | undefined>(undefined);

export function setDisplayTimeZone(ianaTimeZoneId: string | null | undefined, name?: string | null): void {
  displayTimeZone.set(ianaTimeZoneId?.trim() || undefined);
  displayTimeZoneName.set(name?.trim() || undefined);
}

/** A raw UTC offset like "GMT+5" / "GMT-04:00" — what Intl falls back to when a zone has no name. */
const OFFSET_NAME_PATTERN = /^(GMT|UTC)[+-]\d/;

function zoneNamePart(ianaTimeZoneId: string, at: Date, style: 'long' | 'longOffset'): string | undefined {
  return new Intl.DateTimeFormat(DISPLAY_LOCALE, { timeZone: ianaTimeZoneId, timeZoneName: style })
    .formatToParts(at)
    .find((part) => part.type === 'timeZoneName')
    ?.value?.trim();
}

function offsetMinutes(ianaTimeZoneId: string, at: Date): number {
  const match = /GMT(?:([+-])(\d{1,2})(?::(\d{2}))?)?/.exec(zoneNamePart(ianaTimeZoneId, at, 'longOffset') ?? '');
  if (!match?.[1]) return 0;
  const minutes = Number(match[2]) * 60 + Number(match[3] ?? 0);
  return match[1] === '-' ? -minutes : minutes;
}

/**
 * A zone's standard-time name, the same all year — "Central Standard Time" for America/Chicago even
 * in summer. Used where no stored state zone id exists (the viewer's own zone in Local mode). Never
 * "Daylight" and never an offset: a zone Intl cannot name falls back to its city ("Karachi").
 */
export function standardZoneName(ianaTimeZoneId: string): string {
  try {
    // Standard time is whichever half of the year has the smaller offset (daylight saving adds an
    // hour), which also handles southern-hemisphere zones whose summer is in January.
    const year = new Date().getUTCFullYear();
    const january = new Date(Date.UTC(year, 0, 1));
    const july = new Date(Date.UTC(year, 6, 1));
    const standardAt =
      offsetMinutes(ianaTimeZoneId, january) <= offsetMinutes(ianaTimeZoneId, july) ? january : july;
    const name = zoneNamePart(ianaTimeZoneId, standardAt, 'long');
    if (name && !OFFSET_NAME_PATTERN.test(name)) {
      return name;
    }
  } catch {
    // Unknown zone id — fall through to the city name.
  }
  return (ianaTimeZoneId.split('/').pop() ?? ianaTimeZoneId).replace(/_/g, ' ');
}

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

const DISPLAY_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
};

const DISPLAY_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit'
};

const DISPLAY_DATETIME_OPTIONS: Intl.DateTimeFormatOptions = {
  ...DISPLAY_DATE_OPTIONS,
  ...DISPLAY_TIME_OPTIONS
};

const DISPLAY_DATETIME_WITH_SECONDS_OPTIONS: Intl.DateTimeFormatOptions = {
  ...DISPLAY_DATETIME_OPTIONS,
  second: '2-digit'
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

interface ParsedDisplayInput {
  date: Date;
  /** A bare calendar date ("2026-04-15") — has no instant, so it must never shift with the zone. */
  dateOnly: boolean;
}

function parseDisplayInput(value: string | Date | null | undefined): ParsedDisplayInput | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : { date: value, dateOnly: false };
  }

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  if (DATE_ONLY_PATTERN.test(trimmed)) {
    const [year, month, day] = trimmed.split('-').map(Number);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return Number.isNaN(parsed.getTime()) ? null : { date: parsed, dateOnly: true };
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : { date: parsed, dateOnly: false };
}

/**
 * The "yyyy-MM-dd" calendar day of a value the API stores as UTC midnight to mean a date with
 * no time component (payment dates, DOB). Rendering such values as instants would show the
 * previous day in any zone west of UTC, so pass the result of this to `formatDisplayDate`.
 */
export function utcCalendarDay(value: string | Date | null | undefined): string | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value.toISOString().slice(0, 10);
  }
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(String(value).trim());
  return match ? match[1] : null;
}

/**
 * Parses a display value into a Date. Bare "yyyy-MM-dd" strings become UTC midnight of that
 * calendar day (so the day is stable regardless of zone); everything else is a real instant.
 */
export function parseDisplayDateInput(value: string | Date | null | undefined): Date | null {
  return parseDisplayInput(value)?.date ?? null;
}

function formatWith(
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions,
  fallback: string,
  labeled = false
): string {
  const parsed = parseDisplayInput(value);
  if (!parsed) return fallback;

  if (parsed.dateOnly) {
    // Calendar date: render its own y/m/d and never a zone label — there is no instant to label.
    return new Intl.DateTimeFormat(DISPLAY_LOCALE, { ...options, timeZone: 'UTC' }).format(parsed.date);
  }

  let label = displayTimeZoneName();
  let text: string;
  try {
    text = new Intl.DateTimeFormat(DISPLAY_LOCALE, { ...options, timeZone: displayTimeZone() }).format(parsed.date);
  } catch {
    // Unknown zone id: degrade to the browser zone rather than throwing mid-render.
    label = undefined;
    text = new Intl.DateTimeFormat(DISPLAY_LOCALE, options).format(parsed.date);
  }

  // The label is the zone's fixed name ("Central Standard Time"), never Intl's seasonal "CDT"/"CST".
  return labeled ? `${text} ${label ?? standardZoneName(browserTimeZone())}` : text;
}

/** Formats as "Apr 15, 2026" in the display timezone. */
export function formatDisplayDate(value: string | Date | null | undefined, fallback = '—'): string {
  return formatWith(value, DISPLAY_DATE_OPTIONS, fallback);
}

/**
 * Formats a calendar day the API stores as UTC midnight (DOB, payment date) as that day,
 * in every display timezone.
 */
export function formatCalendarDate(value: string | Date | null | undefined, fallback = '—'): string {
  return formatDisplayDate(utcCalendarDay(value), fallback);
}

/** Formats as "5:01 PM" in the display timezone. */
export function formatDisplayTime(value: string | Date | null | undefined, fallback = '—'): string {
  return formatWith(value, DISPLAY_TIME_OPTIONS, fallback);
}

/** Formats as "Apr 15, 2026, 5:01 PM" in the display timezone. */
export function formatDisplayDateTime(value: string | Date | null | undefined, fallback = '—'): string {
  return formatWith(value, DISPLAY_DATETIME_OPTIONS, fallback);
}

/** Formats as "Apr 15, 2026, 5:01:09 PM" in the display timezone. */
export function formatDisplayDateTimeWithSeconds(
  value: string | Date | null | undefined,
  fallback = '—'
): string {
  return formatWith(value, DISPLAY_DATETIME_WITH_SECONDS_OPTIONS, fallback);
}

/** Formats as "Apr 15, 2026 Central Standard Time" — date plus the zone name, for reporting views. */
export function formatReportingDate(value: string | Date | null | undefined, fallback = '—'): string {
  return formatWith(value, DISPLAY_DATE_OPTIONS, fallback, true);
}

/** Formats as "Apr 15, 2026, 5:01 PM Central Standard Time" — datetime plus the zone name, for reporting views. */
export function formatReportingDateTime(value: string | Date | null | undefined, fallback = '—'): string {
  return formatWith(value, DISPLAY_DATETIME_OPTIONS, fallback, true);
}
